import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  FlatList,
  StyleSheet,
  Platform,
  Animated,
  useWindowDimensions,
  StyleProp,
  ViewStyle,
  TextInput,
  ActivityIndicator,
} from "react-native";
// NOTE: SK-VOICE-FE uses `react-native-vector-icons/Ionicons` for the search
// glyph. SKCatalog ships its own SVG-based Icon (no native-font dependency), so
// the single search icon below uses that instead — every other line is identical.
import Icon from "../icon";
import { ModalHeader } from "./modalHeader";
import { FontFamily } from "../../theme/typography";
import { useTheme } from "../../context/themeContext";
import type { ColorScheme } from "../../theme/colors";

// =========================
// Types
// =========================
type Item = {
  label: string;
  value: string;
};

type Props = Readonly<{
  placeholder: string;
  value: string;
  items: Item[];
  onSelect: (value: string) => void;
  disabled?: boolean;
  isDark?: boolean;
  compact?: boolean;
  fieldStyle?: StyleProp<ViewStyle>;
  textColor?: string;
  onSearch?: (text: string) => void;
  searchLoading?: boolean;
}>;

// =========================
// Animated Chevron
// =========================
const AnimatedChevron = ({ open, color }: { open: boolean; color: string }) => {
  const rot = useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    Animated.spring(rot, {
      toValue: open ? 1 : 0,
      useNativeDriver: true,
      tension: 280,
      friction: 22,
    }).start();
  }, [open]);

  const rotate = rot.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "180deg"] });

  return (
    <Animated.View style={{ transform: [{ rotate }] }}>
      <View style={[styles.chevron, { borderColor: color }]} />
    </Animated.View>
  );
};

// =========================
// Check Icon
// =========================
const CheckIcon = ({ color }: { color: string }) => (
  <View style={styles.checkWrapper}>
    <View style={[styles.checkLong, { backgroundColor: color }]} />
    <View style={[styles.checkShort, { backgroundColor: color }]} />
  </View>
);

const DropdownItem = ({
  item,
  value,
  dark,
  colors,
  accentColor,
  onSelect,
  closeModal
}: {
  item: Item;
  value: string;
  dark: boolean;
  colors: ColorScheme;
  accentColor: string;
  onSelect: (val: string) => void;
  closeModal: () => void;
}) => {
  const isSelected = item.value === value;
  return (
    <TouchableOpacity
      activeOpacity={0.65}
      style={[
        styles.item,
        isSelected && { backgroundColor: colors.accentSoft },
      ]}
      onPress={() => { onSelect(item.value); closeModal(); }}
    >
      {isSelected && <View style={[styles.selectedAccent, { backgroundColor: accentColor }]} />}
      <Text style={[
        styles.itemText,
        { color: colors.ink2 },
        isSelected && { color: accentColor, fontWeight: "700" }
      ]}>
        {item.label}
      </Text>
      {isSelected && <CheckIcon color={accentColor} />}
    </TouchableOpacity>
  );
};

const TriggerField = ({
  open, placeholder, value, selectedLabel, disabled, dark, colors, compact, fieldStyle, accentColor, onOpen, textColor, innerRef
}: {
  open: boolean;
  placeholder: string;
  value: string;
  selectedLabel?: string;
  disabled: boolean;
  dark: boolean;
  colors: ColorScheme;
  compact: boolean;
  fieldStyle?: StyleProp<ViewStyle>;
  accentColor: string;
  onOpen: () => void;
  textColor?: string;
  innerRef?: React.Ref<any>;
}) => {
  const isPlaceholder = !value || value === "__placeholder__";
  let chevronColor = colors.ink4;
  if (open) chevronColor = accentColor;

  const textStyle = [
    isPlaceholder ? styles.placeholder : styles.text,
    { color: isPlaceholder ? colors.ink4 : colors.ink },
    compact && styles.textCompact,
    // Optional explicit color for the trigger label (overrides placeholder muting).
    textColor ? { color: textColor } : null,
  ];

  const fieldStyles = [
    styles.field,
    { backgroundColor: colors.card, borderColor: colors.lineStrong },
    open && {
      borderColor: accentColor,
      backgroundColor: dark ? colors.paper2 : colors.accentSoft,
      ...(Platform.OS === 'web'
        ? ({ boxShadow: dark ? "0 0 0 3px rgba(59,91,246,0.18)" : "0 0 0 3px rgba(99,102,241,0.12)" } as any)
        : {}),
    },
    compact && styles.fieldCompact,
    disabled && styles.disabled,
    fieldStyle,
  ];

  return (
    <TouchableOpacity
      ref={innerRef}
      activeOpacity={0.75}
      onPress={onOpen}
      disabled={disabled}
      style={fieldStyles}
    >
      <View style={styles.labelContainer}>
        <Text style={textStyle} numberOfLines={1}>
          {selectedLabel ?? placeholder}
        </Text>
      </View>
      <AnimatedChevron open={open} color={chevronColor} />
    </TouchableOpacity>
  );
};

export default function AppDropdown({
  placeholder,
  value,
  items,
  onSelect,
  disabled = false,
  isDark,
  compact = false,
  fieldStyle,
  textColor,
  onSearch,
  searchLoading = false,
}: Props) {
  const { theme } = useTheme();
  const { colors } = theme;
  // Adapt even when a call site forgets to pass isDark — fall back to the theme.
  const dark = isDark ?? theme.isDark;
  const { width, height } = useWindowDimensions();
  const isMobile  = width < 768;

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  // On desktop the menu anchors directly under the trigger; null → fall back to
  // the centred sheet (mobile, or if the trigger can't be measured).
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
  const scaleAnim         = useRef(new Animated.Value(0.95)).current;
  const opacityAnim       = useRef(new Animated.Value(0)).current;
  const overlayAnim       = useRef(new Animated.Value(0)).current;
  const searchRef  = useRef<TextInput>(null);
  const triggerRef = useRef<any>(null);
  const closingRef = useRef(false);

  const selectedItem = items.find((i) => i.value === value);
  const accentColor  = colors.accent;

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    if (onSearch) onSearch(text);
  };

  const runOpenAnim = useCallback(() => {
    setOpen(true);
    Animated.parallel([
      Animated.spring(scaleAnim, { toValue: 1, useNativeDriver: true, tension: 320, friction: 22 }),
      Animated.timing(opacityAnim, { toValue: 1, duration: 110, useNativeDriver: true }),
      Animated.timing(overlayAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
    ]).start();
  }, [scaleAnim, opacityAnim, overlayAnim]);

  const openModal = useCallback(() => {
    const node = triggerRef.current;
    // Desktop: measure the trigger so the menu opens right under it.
    if (!isMobile && node) {
      if (typeof node.measureInWindow === 'function') {
        node.measureInWindow((x: number, y: number, w: number, h: number) => {
          setAnchor(w > 0 ? { top: y + h + 6, left: x, width: w } : null);
          runOpenAnim();
        });
        return;
      }
      if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
        const r = node.getBoundingClientRect();
        setAnchor(r.width > 0 ? { top: r.top + r.height + 6, left: r.left, width: r.width } : null);
        runOpenAnim();
        return;
      }
    }
    setAnchor(null);
    runOpenAnim();
  }, [isMobile, runOpenAnim]);

  const closeModal = useCallback(() => {
    closingRef.current = true;
    Animated.parallel([
      Animated.spring(scaleAnim, { toValue: 0.95, useNativeDriver: true, tension: 320, friction: 22 }),
      Animated.timing(opacityAnim, { toValue: 0, duration: 90, useNativeDriver: true }),
      Animated.timing(overlayAnim, { toValue: 0, duration: 100, useNativeDriver: true }),
    ]).start(() => {
      setOpen(false);
      setSearchText("");
      closingRef.current = false;
    });
  }, [scaleAnim, opacityAnim, overlayAnim]);

  // Auto-focus search input when modal opens (web only).
  useEffect(() => {
    if (!open || Platform.OS !== 'web') return;
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => searchRef.current?.focus());
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);


  // Handle keyboard events while dropdown is open (web only).
  // - Escape: close the dropdown
  // - Backspace: only preventDefault when focus is NOT in an input to block
  //   browser-back navigation. Do NOT stopPropagation — that would block the
  //   event from reaching the search input, breaking text deletion.
  useEffect(() => {
    if (!open || Platform.OS !== 'web') return;
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
        return;
      }
      if (e.key !== 'Backspace') return;

      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (!isInput) {
        e.preventDefault(); // Prevent browser-back navigation
      } else {
        // When input is empty, nothing gets deleted but the browser can still
        // fire a navigation signal — prevent that without blocking text deletion.
        const inputVal = (target as HTMLInputElement).value ?? '';
        if (inputVal === '') e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeydown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeydown, { capture: true });
  }, [open, closeModal]);

  const filteredItems = items.filter(item =>
    item.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const modalMaxWidth = isMobile ? width - 32 : Math.min(360, width * 0.5);

  // Anchored (desktop) positioning under the trigger, clamped to the viewport.
  const anchored = !!anchor && !isMobile;
  const menuWidth = anchored ? Math.max(anchor!.width, 240) : modalMaxWidth;
  const menuLeft = anchored ? Math.min(Math.max(8, anchor!.left), width - 8 - menuWidth) : 0;
  const menuTop = anchored ? anchor!.top : 0;
  const menuMaxHeight = anchored ? Math.max(180, height - menuTop - 16) : 360;

  const menuCard = (
    <Animated.View
      style={[
        styles.modal,
        { backgroundColor: colors.card },
        dark && { borderWidth: 1, borderColor: colors.line },
        { width: menuWidth, maxHeight: menuMaxHeight, opacity: opacityAnim, transform: [{ scale: scaleAnim }] },
      ]}
    >
      {!anchored && <View style={[styles.dragHandle, { backgroundColor: colors.lineStrong }]} />}
      <ModalHeader
        placeholder={placeholder} count={items.length}
        isDark={dark} accentColor={accentColor} onClose={closeModal}
      />

      <View style={[styles.searchContainer, { backgroundColor: colors.paper2, borderColor: colors.line }]}>
        <Icon
          name="search"
          size={18}
          color={colors.ink4}
          style={styles.searchIcon}
        />
        <TextInput
          ref={searchRef}
          placeholder="Search..."
          placeholderTextColor={colors.ink4}
          value={searchText}
          onChangeText={handleSearchChange}
          style={[styles.searchInput, { color: colors.ink }]}
          autoCapitalize="none"
          autoCorrect={false}
          onBlur={() => {
            if (open && !closingRef.current && Platform.OS === 'web') {
              requestAnimationFrame(() => {
                if (!closingRef.current) searchRef.current?.focus();
              });
            }
          }}
        />
        {searchLoading && (
          <ActivityIndicator size="small" color={accentColor} style={{ marginLeft: 8 }} />
        )}
      </View>

      <FlatList
        data={filteredItems}
        keyExtractor={(i) => i.value}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.listContent}
        renderItem={({ item }) => (
          <DropdownItem
            item={item} value={value} dark={dark} colors={colors}
            accentColor={accentColor} onSelect={onSelect} closeModal={closeModal}
          />
        )}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: colors.ink3 }]}>
              No matches found
            </Text>
          </View>
        }
      />
    </Animated.View>
  );

  return (
    <>
      <TriggerField
        innerRef={triggerRef}
        open={open} placeholder={placeholder} value={value}
        selectedLabel={selectedItem?.label} disabled={disabled}
        dark={dark} colors={colors} compact={compact} fieldStyle={fieldStyle}
        accentColor={accentColor} onOpen={openModal} textColor={textColor}
      />

      <Modal transparent visible={open} animationType="none" onRequestClose={Platform.OS === 'web' ? () => {} : closeModal}>
        {/* Backdrop — dimmed when centred, transparent when anchored. */}
        <Animated.View
          style={[styles.overlay, { opacity: overlayAnim, backgroundColor: anchored ? 'transparent' : 'rgba(5, 8, 20, 0.65)' }]}
          pointerEvents="box-none"
        >
          <TouchableOpacity style={StyleSheet.absoluteFill} onPress={closeModal} activeOpacity={1} />
        </Animated.View>

        {anchored ? (
          <View style={{ position: 'absolute', top: menuTop, left: menuLeft }} pointerEvents="box-none">
            {menuCard}
          </View>
        ) : (
          <View style={styles.overlayCenter} pointerEvents="box-none">
            {menuCard}
          </View>
        )}
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  field: {
    minHeight: 56, borderWidth: 1.5, borderRadius: 2,
    paddingHorizontal: 18, flexDirection: "row",
    alignItems: "center", justifyContent: "space-between",
    ...Platform.select({
      ios:     { shadowColor: "#000", shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.04, shadowRadius: 8 },
      android: { elevation: 2 },
      web:     ({ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" } as any),
    }),
  },
  fieldCompact: { minHeight: 34, borderWidth: 1, borderRadius: 8, paddingHorizontal: 10 },
  disabled: { opacity: 0.45 },
  labelContainer: { flex: 1, marginRight: 8 },
  text:            { fontSize: 15, fontWeight: "600", fontFamily: FontFamily.sans },
  textCompact:     { fontSize: 13, fontWeight: "500", fontFamily: FontFamily.sans },
  placeholder:     { fontSize: 15, fontWeight: "500", fontFamily: FontFamily.sans },
  overlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(5, 8, 20, 0.65)" },
  overlayCenter: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: "center", alignItems: "center", padding: 16, pointerEvents: "box-none" as any },
  modal: {
    borderRadius: 12, maxHeight: 360, paddingBottom: 12,
    ...Platform.select({
      ios:     { shadowColor: "#6366F1", shadowOffset: { width: 0, height: 24 }, shadowOpacity: 0.18, shadowRadius: 48 },
      android: { elevation: 24 },
      web:     ({ boxShadow: "0 24px 64px rgba(0,0,0,0.22)" } as any),
    }),
  },
  dragHandle: { width: 36, height: 4, borderRadius: 2, alignSelf: "center", marginTop: 12, marginBottom: 4, opacity: 0.4 },
  listContent: { padding: 10 },
  item: { flexDirection: "row", alignItems: "center", paddingVertical: 14, paddingHorizontal: 14, borderRadius: 2, marginBottom: 2, overflow: "hidden", position: "relative" },
  selectedAccent: { position: "absolute", left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2 },
  itemText:     { flex: 1, fontSize: 15, fontWeight: "500", paddingLeft: 6, fontFamily: FontFamily.sans },
  chevron: { width: 9, height: 9, borderBottomWidth: 2, borderRightWidth: 2, transform: [{ rotate: "45deg" }], marginBottom: 4 },
  checkWrapper: { width: 20, height: 20, position: "relative" },
  checkLong: { position: "absolute", width: 12, height: 2, bottom: 6, right: 2, transform: [{ rotate: "-45deg" }], borderRadius: 2 },
  checkShort: { position: "absolute", width: 6, height: 2, bottom: 4, left: 4, transform: [{ rotate: "45deg" }], borderRadius: 2 },

  /* Search Styles */
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 4,
    borderRadius: 2,
    paddingHorizontal: 12,
    borderWidth: 1.5,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 44,
    fontSize: 14,
    fontWeight: '500',
    fontFamily: FontFamily.sans,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: FontFamily.sans,
  },
});
